/**
 * Grosveld — tekst die binnenkomt in plaats van er al te staan.
 *
 * Eén IntersectionObserver voor de hele pagina. Geen scroll-listener, geen
 * library, alleen `opacity` en `transform` — de twee eigenschappen die de
 * compositor zonder layout aankan.
 *
 * WAT ER MEEDOET is bewust smal gehouden: per kop de kop zelf plus wat er
 * direct naast staat (intro, knop), tot aan de volgende kop. Daardoor kan dit
 * bestand per constructie niet aan een prijs, een productkaart of een koopknop
 * komen — die zijn nooit een kop en staan er ook niet als broertje naast. Wie
 * hier iets aan verbreedt, haalt die garantie weg.
 *
 * ELKE KOP IS ZIJN EIGEN AANLEIDING. Eerst keek dit bestand naar de sectie en
 * onthulde het alleen de eerste kop daarin; op een blogartikel met negen
 * tussenkoppen bewoog dan alleen de titel. Nu wordt elke groep apart
 * waargenomen, waardoor een lange tekstpagina onderweg net zo leest als de
 * homepage.
 *
 * DE STARTTOESTAND (`opacity: 0`) hangt aan `.gv-motion` op <html>, dat inline
 * in layout/theme.liquid wordt gezet — vóór de eerste paint, anders zie je de
 * tekst eerst staan en dan wegspringen. Draait dít bestand niet, dan haalt de
 * failsafe daar die klasse na twee seconden weg en staat alles gewoon
 * zichtbaar. Een onthulling die niet afvuurt mag nooit tekst wegpoetsen.
 */

// Het teken waar de failsafe in layout/theme.liquid op wacht.
window.grosveldOnthulActief = true;

const html = document.documentElement;

/** Alles zichtbaar laten en verder niets doen. */
function laatStaan() {
  html.classList.remove('gv-motion');
}

if (
  // In de theme editor lijkt verborgen tekst al gauw verdwenen tekst.
  window.Shopify?.designMode ||
  typeof IntersectionObserver === 'undefined' ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
  // Dezelfde ondergrens die assets/view-transitions.js voor de
  // paginaovergangen aanhoudt. `deviceMemory` bestaat niet overal; dan is dit
  // NaN <= 2 en dus false, precies zoals daar.
  Number(navigator.hardwareConcurrency) <= 2 ||
  Number(navigator.deviceMemory) <= 2
) {
  laatStaan();
} else {
  onthul();
}

function onthul() {
  /** Wat als kop mag gelden. h6 is in dit thema de eyebrow, die mag voorop. */
  const KOP =
    'h1, h2, h3, h4, h5, h6, .text-block.h1, .text-block.h2, .text-block.h3, .text-block.h4, .text-block.h5, .text-block.h6';

  /** Wat er naast een kop mag meekomen. */
  const NAAST = '.text-block, .rte, rte-formatter, p';

  /**
   * Nooit binnen deze — en `closest()` kijkt ook naar het element zelf, dus een
   * kop die zélf een van deze klassen draagt valt hier ook af.
   *
   * Let op de dubbele notaties: `product-information` is hier zowel een
   * elementnaam als een klasse op een gewone div, en alleen de tagnaam
   * opschrijven liet de producttitel op de PDP er gewoon doorheen. Gemeten,
   * niet aangenomen. Hetzelfde geldt voor `.slideshow-section`, dat niets met
   * de selector `.slideshow` te maken heeft.
   */
  const NOOIT_BINNEN =
    // Klikbaar of bedienbaar
    'a, button, dialog, summary, label, ' +
    // Herhaalde items
    '.card, product-card, .product-grid, ' +
    // De hero en andere carrousels: staan boven de vouw of bewegen zelf al
    'slideshow-component, .slideshow, .slideshow-section, scroll-container, ' +
    // Het koopblok op de PDP, van buiten naar binnen
    'product-information, .product-information, product-component, .product-details, ' +
    'product-form-component, .quick-add, cart-drawer-component, ' +
    // Bediening: filters mogen nooit wachten op een animatie
    '.facets, .grosveld-actiebalk, ' +
    // Koppen die alleen voor de schermlezer bestaan hebben niets te onthullen
    '.visually-hidden';

  /** Hoeveel elementen één groep hoogstens beslaat: de kop plus wat eronder hoort. */
  const MAX_GROEP = 6;

  /** Hoeveel groepen we hoogstens waarnemen. Een artikel komt rond de tien uit. */
  const MAX_GROEPEN = 40;

  /** Van aanleiding naar de elementen die eraan hangen. */
  const groepen = new WeakMap();

  /**
   * Niet het document scrollt in dit thema, maar `.page-wrapper` met
   * `overflow-y: auto`. Dat is geen detail: een tussenliggende scrollbak knipt
   * alles weg wat eruit gescrold is, en een waarnemer die naar het venster
   * kijkt krijgt daar niets meer van te horen. Vandaar dat de wrapper zelf de
   * root wordt — dan geldt de marge hieronder ook echt.
   */
  const bak = document.querySelector('.page-wrapper');
  const root = bak && /^(auto|scroll)$/.test(getComputedStyle(bak).overflowY) ? bak : null;

  /**
   * De onderkant is de streep: tien procent naar binnen, zodat het niet al
   * gebeurd is voor je kijkt. De bovenkant staat bewust absurd ver open.
   *
   * Dat tweede is geen truc om truc-wille. Een IntersectionObserver meldt
   * niets als een element van "nog niet in beeld" naar "al voorbij" gaat
   * zónder er doorheen te komen, en bij een sprong naar een anker, een
   * herstelde scrollpositie of een terugkeer uit de bfcache gebeurt precies
   * dat. Met deze bovenmarge is "al voorbij" gewoon een vorm van in beeld.
   * Gemeten op de homepage: zonder dit bleven na een terugnavigatie
   * vierentwintig elementen voorgoed onzichtbaar.
   */
  const waarnemer = new IntersectionObserver(
    (regels) => {
      for (const regel of regels) {
        if (!regel.isIntersecting) continue;
        for (const element of groepen.get(regel.target) || [regel.target]) {
          element.classList.add('gv-in');
        }
        waarnemer.unobserve(regel.target);
      }
    },
    { root, rootMargin: '99999px 0px -10% 0px' }
  );

  let aantal = 0;

  // Alleen de sjabloonsecties. Header en footer staan buiten <main> en doen
  // dus per definitie niet mee.
  for (const sectie of document.querySelectorAll('#MainContent > .shopify-section')) {
    for (const kop of sectie.querySelectorAll(KOP)) {
      if (aantal >= MAX_GROEPEN) break;

      if (kop.closest(NOOIT_BINNEN)) continue;

      // Een kop die binnen een al gekozen element valt zou de verschuiving
      // dubbel meekrijgen: zes pixels van de ouder plus zes van zichzelf.
      // `closest` telt het element zelf mee, vandaar vanaf de ouder.
      if (kop.parentElement?.closest('.gv-onthul')) continue;

      const groep = [kop];
      let broer = kop.nextElementSibling;

      while (broer && groep.length < MAX_GROEP) {
        // Bij de volgende kop houdt deze groep op; die begint zijn eigen.
        if (broer.matches(KOP)) break;
        // Een buur die zelf beeld of kaarten bevat is geen intro maar de inhoud
        // van de sectie. Die laten we staan.
        if (broer.matches(NAAST) && !broer.querySelector('img, video, .card, product-card')) {
          groep.push(broer);
        }
        broer = broer.nextElementSibling;
      }

      // Geen trap: kop en intro komen samen. Een zichtbare volgorde leest als
      // een effect, en dit hoort alleen gladder te voelen dan tekst die er
      // ineens staat.
      for (const element of groep) element.classList.add('gv-onthul');

      groepen.set(kop, groep);
      waarnemer.observe(kop);
      aantal++;
    }

    if (aantal >= MAX_GROEPEN) break;
  }
}
